"use client";

import { lazyComponent } from "@/components/ui/lazy-component";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useHeroMapContext } from "@/components/home/context/hero-map-context";
import { useListingBrowseContext } from "./context/listing-browse-context";
import { HorizontalFilterBar } from "./filter-bar/horizontal-filter-bar";
import { getColumnsForProductType } from "./table/columns";
import { effectiveColumnVisibility } from "./table/column-visibility";
import { LgUpOnly } from "@/components/responsive/breakpoint-gate";
import { ListingPagination } from "./listing-pagination";
import { ResultsHeader } from "./results-header";
import { ListingMapPane, mapScopeNote, useClusterListings } from "./listing-map-pane";
import { useListingBrowseProducts } from "./use-listing-browse-products";
import { useListingUrlSync } from "./use-listing-url-sync";
import { useListingResultTracking } from "./use-listing-result-tracking";
import { CardGrid } from "./card-grid";
import {
  CatalogueMapColumn,
  CatalogueMobileMap,
  CatalogueShell,
  scrollCatalogueToTop,
} from "./catalogue-shell";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { ActionErrorAlert } from "@/components/ui/action-error-alert";
import { ListingEmptyState } from "./empty-state";
import { LISTING_BROWSE_PAGE_SIZE as PAGE_SIZE } from "./listing-browse-query";
import type { BrowseListing } from "@/components/listing/browse/marketplace-listing";
import type { ClusterListing } from "@/components/location/listings-cluster-map";

const ListingRecordTable = lazyComponent(
  () =>
    import("./listing-record-table").then(
      (module) => module.ListingRecordTable,
    ),
  {
    loading: <p role="status">Loading listings…</p>,
  },
);
const ColumnToggle = lazyComponent(
  () =>
    import("@/components/ui/record-table/column-toggle").then(
      (module) => module.ColumnToggle<BrowseListing>,
    ),
  {
    loading: <p role="status">Loading columns…</p>,
  },
);

// WHY: two passes, because scroll anchoring re-pins the offset the moment the outgoing page's cards are replaced. Paired with `overflow-anchor: none` on the pane.
function useScrollToTopOnPageChange(page: number, firstResultId?: string) {
  const resultsRef = useRef<HTMLElement | null>(null);
  const previousPage = useRef(page);
  const awaitingResults = useRef(false);

  useEffect(() => {
    if (previousPage.current === page) return;
    previousPage.current = page;
    awaitingResults.current = true;
    scrollCatalogueToTop(resultsRef.current);
  }, [page]);

  useEffect(() => {
    if (!awaitingResults.current) return;
    awaitingResults.current = false;
    scrollCatalogueToTop(resultsRef.current);
  }, [firstResultId]);

  return resultsRef;
}

function DesktopMapColumn({
  listings,
  scopeNote,
}: {
  listings: ClusterListing[];
  scopeNote: string | null;
}) {
  return (
    <CatalogueMapColumn>
      <LgUpOnly>
        <ListingMapPane listings={listings} scopeNote={scopeNote} />
      </LgUpOnly>
    </CatalogueMapColumn>
  );
}

function CompactMapSection({
  listings,
  scopeNote,
  open,
  onOpenChange,
}: {
  listings: ClusterListing[];
  scopeNote: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <CatalogueMobileMap
      overlayTitle="Listings on map"
      open={open}
      onOpenChange={onOpenChange}
    >
      <ListingMapPane listings={listings} scopeNote={scopeNote} />
    </CatalogueMobileMap>
  );
}

function pageCountFor(
  totalCount: number | null,
  page: number,
  hasNextPage: boolean,
): number {
  return totalCount != null
    ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
    : page + (hasNextPage ? 1 : 0);
}

interface ListingBrowseContentProps {
  initialListings?: BrowseListing[];
  initialTotalCount?: number;
  seoLinks?: ReactNode;
  header?: ReactNode;
}

// WHY: a total under a listed-within filter tells buyers how old the rest of the catalogue is.
function shownTotal(totalCount: number | null, listedAfterDays: number | null) {
  return listedAfterDays === null ? totalCount : null;
}

function mapNoteFor(
  data: ReturnType<typeof useListingBrowseProducts>,
  listedAfterDays: number | null,
) {
  return mapScopeNote({
    page: data.page,
    pageSize: PAGE_SIZE,
    loadedCount: data.products.length,
    totalCount: shownTotal(data.listingTotal, listedAfterDays),
    hasNextPage: data.hasNextPage,
  });
}

function useBrowseResults({
  initialListings,
  initialTotalCount,
}: ListingBrowseContentProps) {
  const heroMap = useHeroMapContext();
  const listingBrowse = useListingBrowseContext();
  const data = useListingBrowseProducts({
    location: heroMap.location,
    radiusMiles: heroMap.radiusMiles ?? 200,
    quantity: heroMap.quantity || 1,
    productType: listingBrowse.productType,
    conditions: listingBrowse.conditions,
    priceRange: listingBrowse.priceRange,
    specFilters: listingBrowse.specFilters,
    stateCode: listingBrowse.stateCode,
    listedAfterDays: listingBrowse.listedAfterDays,
    intentFilter: listingBrowse.intentFilter,
    searchQuery: listingBrowse.searchQuery,
    saleOnly: listingBrowse.saleOnly,
    initialListings,
    initialTotalCount,
  });
  useListingUrlSync();
  const trackSelect = useListingResultTracking(data);
  const columns = useMemo(
    () =>
      getColumnsForProductType(
        listingBrowse.productType,
        heroMap.quantity,
        trackSelect,
      ),
    [listingBrowse.productType, heroMap.quantity, trackSelect],
  );
  const mapListings = useClusterListings(data.products, heroMap.quantity);
  return {
    ...data,
    totalCount: shownTotal(data.totalCount, listingBrowse.listedAfterDays),
    listingTotal: shownTotal(data.listingTotal, listingBrowse.listedAfterDays),
    columns,
    mapListings,
    mapNote: mapNoteFor(data, listingBrowse.listedAfterDays),
    trackSelect,
    handleHover: heroMap.setHoveredProductId,
    requestedQuantity: heroMap.quantity,
    hoveredRowId: heroMap.hoveredProductId,
    mapVisible: listingBrowse.mapVisible,
    mapAvailable: listingBrowse.mapAvailable,
  };
}

type BrowseResults = ReturnType<typeof useBrowseResults>;

export function ListingBrowseContent({
  header,
  seoLinks,
  ...initial
}: ListingBrowseContentProps) {
  const results = useBrowseResults(initial);
  const [phoneMapOpen, setPhoneMapOpen] = useState(false);
  const paneRef = useScrollToTopOnPageChange(
    results.page,
    results.products[0]?._id,
  );
  return (
    <CatalogueShell
      header={header}
      filterBar={
        <HorizontalFilterBar
          onOpenMap={() => setPhoneMapOpen(true)}
          resultCount={results.listingTotal}
        />
      }
      mapColumn={
        results.mapVisible ? (
          <DesktopMapColumn
            listings={results.mapListings}
            scopeNote={results.mapNote}
          />
        ) : null
      }
      paneRef={paneRef}
      after={seoLinks}
    >
      <BrowseResultsPane
        results={results}
        phoneMapOpen={phoneMapOpen}
        onPhoneMapOpenChange={setPhoneMapOpen}
      />
    </CatalogueShell>
  );
}

function BrowseResultsControls({ results }: { results: BrowseResults }) {
  const [columnEditorOpen, setColumnEditorOpen] = useState(false);
  const { columnVisibility, setColumnVisibility } = useListingBrowseContext();
  return (
    <>
      <ResultsHeader
        totalCount={results.listingTotal}
        loadedCount={results.products.length}
        page={results.page}
        truncated={results.truncated}
        isExpandedSearch={results.isExpandedSearch}
        isLoading={results.isLoading}
        onToggleColumnEditor={() => setColumnEditorOpen(!columnEditorOpen)}
      />
      {columnEditorOpen ? (
        <ErrorBoundary
          label="listing-column-editor"
          fallback={
            <ActionErrorAlert
              error="Column controls could not load. Refresh to try again."
              onDismiss={() => setColumnEditorOpen(false)}
              reportable={false}
            />
          }
        >
          <ColumnToggle
            columns={results.columns}
            visibility={effectiveColumnVisibility(
              results.columns,
              results.products,
              columnVisibility,
            )}
            onVisibilityChange={setColumnVisibility}
            onClose={() => setColumnEditorOpen(false)}
          />
        </ErrorBoundary>
      ) : null}
    </>
  );
}

function BrowseResultsPane({
  results,
  phoneMapOpen,
  onPhoneMapOpenChange,
}: {
  results: BrowseResults;
  phoneMapOpen: boolean;
  onPhoneMapOpenChange: (open: boolean) => void;
}) {
  return (
    <>
      <BrowseResultsControls results={results} />
      <ListingResults
        requestedQuantity={results.requestedQuantity}
        products={results.products}
        isLoading={results.isLoading}
        mapVisible={results.mapVisible}
        columns={results.columns}
        hoveredRowId={results.hoveredRowId}
        onHoverRow={results.handleHover}
        onSelect={results.trackSelect}
      />
      <ListingPagination
        page={results.page}
        totalPages={pageCountFor(
          results.totalCount,
          results.page,
          results.hasNextPage,
        )}
        endKnown={results.totalCount != null}
        listingTotal={results.listingTotal}
        isLoading={results.isLoading}
        onPageChange={results.setPage}
      />
      {results.mapAvailable ? (
        <CompactMapSection
          listings={results.mapListings}
          scopeNote={results.mapNote}
          open={phoneMapOpen}
          onOpenChange={onPhoneMapOpenChange}
        />
      ) : null}
    </>
  );
}

interface ListingResultsProps {
  requestedQuantity?: number;
  products: BrowseListing[];
  isLoading: boolean;
  mapVisible: boolean;
  columns: ReturnType<typeof getColumnsForProductType>;
  hoveredRowId: string | null;
  onHoverRow: (id: string | null) => void;
  onSelect: (product: BrowseListing) => void;
}

function ListingResultsTable({
  products,
  isLoading,
  columns,
  hoveredRowId,
  onHoverRow,
}: Omit<ListingResultsProps, "mapVisible">) {
  return (
    <div className="mx-auto w-full max-w-[100rem] px-4 py-4 @3xl:px-8">
      <ListingRecordTable
        products={products}
        columns={columns}
        isLoading={isLoading}
        activeRowId={hoveredRowId}
        onRowHover={onHoverRow}
      />
    </div>
  );
}

function ListingResults({ mapVisible, ...rest }: ListingResultsProps) {
  const { viewMode } = useListingBrowseContext();

  if (!rest.isLoading && rest.products.length === 0) {
    return <ListingEmptyState />;
  }

  const grid = (
    <CardGrid
      products={rest.products}
      isLoading={rest.isLoading}
      mapVisible={mapVisible}
      hoveredRowId={rest.hoveredRowId}
      onHoverRow={rest.onHoverRow}
      onSelect={rest.onSelect}
      requestedQuantity={rest.requestedQuantity}
    />
  );

  if (viewMode === "table") {
    return (
      <ErrorBoundary
        label="listing-table"
        fallback={
          <>
            <ActionErrorAlert
              error="Table view could not load. Showing listing cards instead."
              reportable={false}
            />
            {grid}
          </>
        }
      >
        <ListingResultsTable {...rest} />
      </ErrorBoundary>
    );
  }
  return grid;
}
